import { useContext, useMemo, useState } from "react";
import PropTypes from "prop-types";
import UserContext from "common/Context/UserContext";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import ClearIcon from "@mui/icons-material/Clear";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import { styled } from "@mui/material/styles";
import SelectParentDialog from "common/Components/BulkEditNodeListDialog/SelectParentDialog";
import { buildAllowedParentTree } from "common/Components/BulkEditNodeListDialog/branchTreeData";
import {
  FormTextField,
  GenericFormFieldHintText,
} from "styles/formStyleComponents";
import {
  formatParentNameWithId,
  resolveParentCandidatesForNodeType,
} from "pages/DataManagement/NodeList/nodeListRowsMapper";
import {
  isFiniteNumber,
  isNullUndefinedOrEmptyString,
} from "common/utils/valueChecks";

const PickerWrap = styled("div")({
  flex: 1,
  minWidth: 0,
  width: "100%",
  display: "flex",
  flexDirection: "column",
});

const PickerRow = styled("div")({
  display: "flex",
  alignItems: "center",
  gap: 6,
  minWidth: 0,
});

const PickerFieldWrap = styled("div")({
  flex: 1,
  minWidth: 0,
});

const PickerActionButton = styled(IconButton)({
  width: 32,
  height: 32,
  flex: "0 0 auto",
  borderRadius: 7,
});

export default function ParentNodeTreePicker({
  label,
  hint,
  nodeType,
  referencePayload,
  parentCandidates,
  disabled,
  valueId,
  displayNameOverride,
  onChange,
  inputTestId,
  placeholder,
  error,
}) {
  const { getCustomerName } = useContext(UserContext);
  const [dialogOpen, setDialogOpen] = useState(false);
  const customerName = getCustomerName?.() ?? "";
  const hintId = inputTestId ? `${inputTestId}-hint` : "parent-node-hint";

  const resolvedParentCandidates = useMemo(
    () =>
      resolveParentCandidatesForNodeType(
        referencePayload,
        nodeType,
        parentCandidates,
        customerName
      ),
    [referencePayload, nodeType, parentCandidates, customerName]
  );

  const treeRoot = useMemo(
    () =>
      buildAllowedParentTree(
        referencePayload,
        nodeType,
        parentCandidates,
        customerName
      ),
    [referencePayload, nodeType, parentCandidates, customerName]
  );

  const displayText = useMemo(() => {
    if (isNullUndefinedOrEmptyString(valueId)) return "";
    if (!isFiniteNumber(valueId)) return "";
    const idNum = Number(valueId);
    const match = resolvedParentCandidates.find(
      (c) => Number(c.nodeId) === idNum
    );
    const nameFromCandidate =
      match?.nodeName != null && String(match.nodeName).trim() !== ""
        ? String(match.nodeName).trim()
        : "";
    const override =
      displayNameOverride != null && String(displayNameOverride).trim() !== ""
        ? String(displayNameOverride).trim()
        : "";
    const namePart = nameFromCandidate || override;
    return formatParentNameWithId(namePart, idNum);
  }, [valueId, resolvedParentCandidates, displayNameOverride]);

  const canBrowse = Boolean(nodeType) && !disabled && treeRoot;

  const handleConfirm = ({ parentNodeId }) => {
    const id = Number(parentNodeId);
    if (!Number.isFinite(id)) {
      onChange?.({ parentNodeId: null, parentNode: "" });
      return;
    }
    onChange?.({ parentNodeId: id, parentNode: String(id) });
  };

  const handleClear = (event) => {
    event.stopPropagation();
    onChange?.({ parentNodeId: null, parentNode: "" });
  };

  return (
    <PickerWrap>
      <PickerRow>
        <PickerFieldWrap>
          <FormTextField
            size="small"
            fullWidth
            label={label}
            disabled={disabled}
            value={displayText}
            placeholder={placeholder}
            error={!!error}
            helperText={error || undefined}
            aria-describedby={hint && !error ? hintId : undefined}
            InputProps={{
              readOnly: true,
              inputProps: inputTestId
                ? { "data-testid": `${inputTestId}-display` }
                : undefined,
            }}
          />
        </PickerFieldWrap>
        {displayText && !disabled ? (
          <Tooltip title="Clear parent">
            <PickerActionButton
              size="small"
              aria-label="Clear node parent"
              onClick={handleClear}
            >
              <ClearIcon fontSize="small" />
            </PickerActionButton>
          </Tooltip>
        ) : null}
        <Tooltip
          title={
            canBrowse
              ? "Open searchable hierarchy to pick a parent"
              : !nodeType
                ? "Select a node type first"
                : !treeRoot
                  ? "No valid parent branches for this type"
                  : ""
          }
        >
          <span>
            <PickerActionButton
              size="small"
              disabled={!canBrowse}
              aria-label="Select parent from tree"
              onClick={() => setDialogOpen(true)}
              data-testid={inputTestId}
            >
              <AccountTreeOutlinedIcon fontSize="small" />
            </PickerActionButton>
          </span>
        </Tooltip>
      </PickerRow>
      {hint && !error ? (
        <GenericFormFieldHintText id={hintId} variant="caption">
          {hint}
        </GenericFormFieldHintText>
      ) : null}
      <SelectParentDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onConfirm={handleConfirm}
        initialValue={displayText}
        treeRoot={treeRoot}
        loading={false}
      />
    </PickerWrap>
  );
}

ParentNodeTreePicker.propTypes = {
  label: PropTypes.string,
  hint: PropTypes.string,
  nodeType: PropTypes.string,
  referencePayload: PropTypes.object,
  parentCandidates: PropTypes.arrayOf(PropTypes.object),
  disabled: PropTypes.bool,
  valueId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  displayNameOverride: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  inputTestId: PropTypes.string,
  placeholder: PropTypes.string,
  error: PropTypes.string,
};

ParentNodeTreePicker.defaultProps = {
  label: undefined,
  hint: undefined,
  nodeType: "",
  referencePayload: null,
  parentCandidates: [],
  disabled: false,
  valueId: null,
  displayNameOverride: undefined,
  inputTestId: undefined,
  placeholder: "",
  error: undefined,
};
